// src/components/invoices/RunningAccountSuccessModal.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Chip,
    Divider,
    Alert,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    AccountBalanceWallet as AccountIcon,
    Person as PersonIcon,
    ShoppingCart as CartIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

function RunningAccountSuccessModal({ 
    open, 
    onClose, 
    clientData,
    movementData,
    itemCount,
    total,
    onClearCart,
    onKeepCart,
    onGoToRunningAccount
}) {
    // Format currency
    const formatPrice = useCallback((price) => {
        if (typeof price !== 'number' || isNaN(price)) {
            return 'N/A';
        }
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    // Get client display name
    const getClientName = () => {
        if (!clientData) return 'Cliente';
        return clientData.business_name || clientData.razon_social || clientData.nombre || 'Cliente';
    };

    // Get client document
    const getClientDocument = () => {
        if (!clientData) return '';
        return clientData.document_number || clientData.documento_nro || clientData.cuit || '';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden'
                }
            }}
        >
            {/* Success Header */}
            <DialogTitle 
                sx={{ 
                    backgroundColor: '#4caf50',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 32 }} />
                    <Typography variant="h5" component="div" fontWeight="bold">
                        ¡Cargado Exitosamente!
                    </Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                {/* Success Message */}
                <Alert 
                    severity="success" 
                    icon={<AccountIcon />}
                    sx={{ mb: 3, fontSize: '1rem' }}
                >
                    Los items han sido agregados a la Cuenta Abierta del cliente.
                    <br />
                    <strong>No se generó factura ni se registró en AFIP.</strong>
                </Alert>

                {/* Client Info Card */}
                <Box 
                    sx={{ 
                        backgroundColor: '#f5f5f5',
                        borderRadius: 2,
                        p: 2.5,
                        mb: 3,
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PersonIcon color="primary" />
                        <Typography variant="subtitle2" color="text.secondary">
                            Cliente de Cuenta Abierta
                        </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {getClientName()}
                    </Typography>
                    {getClientDocument() && (
                        <Chip 
                            label={`CUIT/DNI: ${getClientDocument()}`}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                        />
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Movement Summary */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CartIcon color="action" />
                        <Typography variant="body1" color="text.secondary">
                            Items Cargados:
                        </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                        {itemCount || 1} {(itemCount || 1) === 1 ? 'producto' : 'productos'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        Total del Cargo:
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                        {formatPrice(total || 0)}
                    </Typography>
                </Box>

                {movementData?.movementId && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Chip 
                            label={`Movimiento #${movementData.movementId}`}
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                )}

                {/* Info about next steps */}
                <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                        <strong>Próximos pasos:</strong> Los items quedan pendientes de facturación. 
                        Puede generar la factura desde la pestaña <strong>Cuentas Abiertas</strong> 
                        cuando el cliente desee abonar.
                    </Typography>
                </Alert>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#fafafa', flexDirection: 'column', gap: 2 }}>
                {/* Cart options row */}
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    width: '100%',
                    justifyContent: 'center'
                }}>
                    <Button
                        variant="outlined"
                        onClick={onKeepCart}
                        sx={{ flex: 1, maxWidth: 200 }}
                    >
                        Mantener Carrito
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onClearCart}
                        sx={{ 
                            flex: 1, 
                            maxWidth: 200,
                            backgroundColor: '#f5d540',
                            color: 'black',
                            '&:hover': { backgroundColor: '#e0c030' }
                        }}
                    >
                        Limpiar Carrito
                    </Button>
                </Box>
                
                {/* Go to Running Accounts option */}
                {onGoToRunningAccount && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => onGoToRunningAccount(false)}
                        endIcon={<ArrowForwardIcon />}
                        fullWidth
                        sx={{ maxWidth: 420 }}
                    >
                        Ir a Cuentas Abiertas
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

RunningAccountSuccessModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    clientData: PropTypes.object,
    movementData: PropTypes.object,
    itemCount: PropTypes.number,
    total: PropTypes.number,
    onClearCart: PropTypes.func,
    onKeepCart: PropTypes.func,
    onGoToRunningAccount: PropTypes.func
};

export default RunningAccountSuccessModal;
